import { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  AddImeiDownloadIcon,
  AddImeiUploadIcon,
  AddImeiAddSquareIcon,
  AddImeiReloadIcon,
  CloseIcon,
  DeleteBin6LineIcon,
} from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import deviceApi from '~/apis/device';
import locationApi from '~/apis/location';
import { handleApiError, notify, readExcel } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';
import styles from './AddImeiDialog.module.scss';

const cx = classNames.bind(styles);

type AddImeiDialogProps = {
  open: boolean;
  currentUser?: any;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

function AddImeiDialog(props: AddImeiDialogProps) {
  const { open, currentUser, setOpen } = props;
  const { t } = useTranslation('admin/device');

  const [file, setFile] = useState<File | undefined>(undefined);
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [stagedImeis, setStagedImeis] = useState<string[]>([]);
  const [provinceId, setProvinceId] = useState<string>('');
  const [wardId, setWardId] = useState<string>('');
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;

    setFile(undefined);
    setTextareaValue('');
    setStagedImeis([]);
    setProvinceId(String(currentUser?.province_id || ''));
    setWardId('');

    const loadProvinces = async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          }))
        );
      } catch (error) {
        handleApiError(error);
      }
    };

    loadProvinces();
  }, [open, currentUser?.province_id]);

  useEffect(() => {
    if (!open || !provinceId) {
      setWardOptions([]);
      setWardId('');
      return;
    }

    const loadWards = async () => {
      try {
        const response = await locationApi.getListWards({ province: provinceId });
        setWardOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
            data3: item.parent_code,
          }))
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      }
    };

    loadWards();
  }, [open, provinceId]);

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');
      worksheet.columns = [{ header: 'IMEI', key: 'IMEI', width: 25 }];
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${t('addImeiDialog.templateFileName')}.xlsx`);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleParseFile = async (uploadedFile: File) => {
    try {
      const rows = await readExcel(uploadedFile);
      if (!rows.length) {
        notify('error', t('addImeiDialog.fileNoData'));
        return;
      }
      const imeis = rows.map((row: any) => String(row['IMEI'] ?? row['imei'] ?? '').trim()).filter(Boolean);

      if (!imeis.length) {
        notify('error', t('addImeiDialog.fileNoImeiColumn'));
        return;
      }
      setTextareaValue(imeis.join('\n'));
    } catch (e) {
      handleApiError(e);
    }
  };

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const dropped = acceptedFiles[0];
        setFile(dropped);
        handleParseFile(dropped);
      }
    },
  });

  const handleAddNew = () => {
    if (!textareaValue.trim()) {
      notify('error', t('addImeiDialog.imeiRequired'));
      return;
    }

    const newImeis = textareaValue
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const merged = Array.from(new Set([...stagedImeis, ...newImeis]));
    setStagedImeis(merged);

    setTextareaValue('');
    setFile(undefined);
  };

  const handleReset = () => {
    setTextareaValue('');
    setFile(undefined);
  };

  const handleDeleteImei = (imeiToDelete: string) => {
    setStagedImeis((prev) => prev.filter((imei) => imei !== imeiToDelete));
  };

  const handleSave = async () => {
    if (stagedImeis.length === 0) {
      notify('error', t('addImeiDialog.saveEmpty'));
      return;
    }

    try {
      setIsLoading(true);
      const payload: { imeis: string[]; provinceId?: number | string; wardId?: number | string } = {
        imeis: stagedImeis,
      };

      if (provinceId) {
        payload.provinceId = provinceId;
      }
      if (wardId) {
        payload.wardId = wardId;
      }

      const response = await deviceApi.addImei(payload);
      const { inserted = [], existed = [] } = response.data?.result ?? {};

      if (inserted.length > 0) {
        notify('success', t('addImeiDialog.saveSuccess', { inserted: inserted.length }));
        if (setOpen) setOpen(false, true);
      } else if (existed.length > 0) {
        const existedList = existed.join(', ');
        notify('warning', t('addImeiDialog.allExistedDetail', { list: existedList }));
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setIsLoading(false);
    }
  };
    
  const handleClose = () => {
    if (isLoading) return;
    if (setOpen) setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} PaperProps={{ className: cx('dialog-paper') }}>
      <DialogTitle className={cx('dialog-title-bar')}>
        <Typography className={cx('dialog-title')}>{t('addImeiDialog.title')}</Typography>
        <IconButton size='small' onClick={handleClose} className={cx('close-btn')}>
          <CloseIcon className='w-5 h-5' />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('dialog-content')}>
        <Box className={cx('main-content-wrapper')}>
          <Box className={cx('upload-section')}>
            <Box {...getRootProps()} className={cx('dropzone', { 'dropzone--active': isDragAccept })}>
              <Box component='input' {...getInputProps()} />
              <Box className={cx('dropzone-content')}>
                <Box className={cx('upload-icon-wrapper')}>
                  <AddImeiUploadIcon className={cx('upload-dropzone-icon')} />
                </Box>
                <Typography className={cx('dropzone-hint')}>
                  {file ? file.name : t('addImeiDialog.dragAndDropHint')}
                </Typography>
              </Box>
            </Box>

            <Button
              variant='contained'
              className={cx('download-btn')}
              startIcon={<AddImeiDownloadIcon className={cx('button-icon')} />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadTemplate();
              }}
            >
              {t('addImeiDialog.downloadTemplate')}
            </Button>
          </Box>

          <Box className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <Box>
              <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('province_id')}</Typography>
              <BaseSelect
                options={provinceOptions}
                value={provinceId}
                placeholder={t('select_province_id')}
                isSearchEnable
                isDisplayAllOption
                onChange={(event) => {
                  const nextProvinceId = event.target.value;
                  setProvinceId(nextProvinceId);
                  setWardId('');
                  if (!nextProvinceId) {
                    setWardOptions([]);
                  }
                }}
              />
            </Box>
            <Box>
              <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('ward_id')}</Typography>
              <BaseSelect
                options={wardOptions}
                value={wardId}
                placeholder={t('select_ward_id')}
                isSearchEnable
                isDisplayAllOption
                onChange={(event) => {
                  setWardId(event.target.value);
                }}
                disabled={!provinceId}
              />
            </Box>
          </Box>

          <Box className={cx('imeis-input-section')}>
            <Typography className={cx('section-label')}>
              {t('addImeiDialog.imeiListLabel')}
              <span className='text-red-500'>*</span>
            </Typography>
            <textarea
              className={cx('textarea')}
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              rows={5}
            />
          </Box>

          <Box className={cx('action-buttons')}>
            <Button
              variant='contained'
              className={cx('action-btn', 'add-btn')}
              startIcon={<AddImeiAddSquareIcon className={cx('button-icon')} />}
              onClick={handleAddNew}
            >
              {t('addImeiDialog.addNew')}
            </Button>
            <Button
              variant='contained'
              className={cx('action-btn', 'reset-btn')}
              startIcon={<AddImeiReloadIcon className={cx('button-icon')} />}
              onClick={handleReset}
            >
              {t('addImeiDialog.reset')}
            </Button>
          </Box>
        </Box>

        <Box className={cx('table-section')}>
          <Typography className={cx('total-imei-label')} component='p'>
            {t('addImeiDialog.totalImei')}&nbsp;
            <span className={cx('total-imei-count')}>{stagedImeis.length}</span>
          </Typography>

          <Box className={cx('table-wrapper')}>
            <TableContainer>
              <Table size='small' className={cx('imei-table')}>
                <TableHead>
                  <TableRow>
                    <TableCell className={cx('table-head-cell')} width={60}>
                      {t('addImeiDialog.tableNo')}
                    </TableCell>
                    <TableCell className={cx('table-head-cell')}>{t('addImeiDialog.tableImei')}</TableCell>
                    <TableCell className={cx('table-head-cell')} width={72} align='center'>
                      {t('addImeiDialog.tableDelete')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stagedImeis.length === 0 ? (
                    <TableRow className={cx('table-empty-row')}>
                      <TableCell colSpan={3} className={cx('table-empty')}>
                        {t('addImeiDialog.tableEmpty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    stagedImeis.map((imei, index) => (
                      <TableRow key={imei} hover>
                        <TableCell className={cx('table-body-cell')}>{String(index + 1).padStart(2, '0')}</TableCell>
                        <TableCell className={cx('table-body-cell')}>{imei}</TableCell>
                        <TableCell className={cx('table-body-cell')} align='center'>
                          <IconButton size='small' className={cx('delete-imei-btn')} onClick={() => handleDeleteImei(imei)}>
                            <DeleteBin6LineIcon className='w-4 h-4' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box className={cx('dialog-footer')}>
          <Button variant='outlined' fullWidth onClick={handleClose} disabled={isLoading} className={cx('cancel-btn')}>
            {t('addImeiDialog.cancel')}
          </Button>
          <Button
            variant='contained'
            fullWidth
            disabled={isLoading}
            onClick={handleSave}
            className={cx('save-btn')}
            startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {t('addImeiDialog.save')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default memo(AddImeiDialog);
